import Head from 'next/head'
import { useRecoilValue } from 'recoil'
import { subDataState } from '@/util/states'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import styled, { keyframes } from 'styled-components'
import { urlParser } from '@/util/imgHelpers'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useGetDrops } from 'src/hooks/useGetDrops'

const BEYOND_ISSUE_URL = 'Beyond-the-Pines-Volume-1'
const STANDARD_ISSUE_URL = 'volume-1-standard'

const getIssueByUrl = async (issueUrl: string) => {
  try {
    const res = await fetch('/api/getEntries', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        content_type: 'issue',
        limit: 1,
        options: { 'fields.issueUrl': issueUrl }
      })
    })
    const data = await res.json()
    if (!data || data.length === 0) return null
    
    return {
      id: data[0].sys.id,
      title: data[0].fields.issueTitle,
      issueUrl: data[0].fields.issueUrl,
      cover: data[0].fields.frontCoverPreview
    }
  } catch (err) {
    console.error('Error fetching issue:', err)
    return null
  }
}

export default function Issues() {
  const subData = useRecoilValue(subDataState)
  const router = useRouter()
  const [issueData, setIssueData] = useState<{
    id: string
    title: string
    issueUrl: string
    cover: any
  } | null>(null)
  const [isLoadingIssue, setIsLoadingIssue] = useState(true)
  const [selectedTier, setSelectedTier] = useState<'beyond' | 'standard'>('beyond')

  const isBeyondSubscriber = subData !== 'pending' && subData.tier === 'Beyond'

  // Set default tier based on user's subscription (runs once when subData loads)
  useEffect(() => {
    if (subData === 'pending') return
    setSelectedTier(subData.tier === 'Beyond' ? 'beyond' : 'standard')
  }, [subData])

  // Determine which issue to show based on subscription and toggle
  useEffect(() => {
    if (subData === 'pending') return

    // Not subscribed - redirect to subscribe
    if (!subData.isSubbed) {
      router.push('/subscribe')
      return
    }

    // Choose issue based on tier and toggle selection
    let issueUrl: string
    if (selectedTier === 'beyond') {
      issueUrl = BEYOND_ISSUE_URL
    } else {
      issueUrl = STANDARD_ISSUE_URL
    }
    
    setIsLoadingIssue(true)
    getIssueByUrl(issueUrl).then((data) => {
      setIssueData(data)
      setIsLoadingIssue(false)
    })
  }, [subData, router, selectedTier])

  if (subData === 'pending' || isLoadingIssue) {
    return (
      <PageContainer>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Loading your content...</LoadingText>
        </LoadingContainer>
      </PageContainer>
    )
  }

  if (!issueData) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorText>Unable to load content. Please try again.</ErrorText>
        </ErrorContainer>
      </PageContainer>
    )
  }

  return (
    <>
      <Head>
        <title>{issueData.title} — Beyond The Pines</title>
        <meta
          name="description"
          content={`${issueData.title} for Beyond The Pines Magazine. Breathtaking photography and visual provocation.`}
        />
      </Head>
      <PageContainer>
        <Header>
          <TierToggle>
            <ToggleOption
              active={selectedTier === 'standard'}
              onClick={() => setSelectedTier('standard')}
            >
              <ToggleIcon src="/images/standard-icon.png" alt="Standard" />
              <span>Standard</span>
            </ToggleOption>
            <ToggleOption
              active={selectedTier === 'beyond'}
              onClick={() => setSelectedTier('beyond')}
              locked={!isBeyondSubscriber}
            >
              <ToggleIcon src="/images/beyond-icon.png" alt="Beyond" />
              <span>Beyond</span>
              {!isBeyondSubscriber && <LockIcon>🔒</LockIcon>}
            </ToggleOption>
          </TierToggle>
        </Header>
        
        {/* Show upgrade prompt for Standard users trying to view Beyond content */}
        {selectedTier === 'beyond' && !isBeyondSubscriber ? (
          <UpgradePrompt onUpgrade={() => router.push('/subscribe')} />
        ) : (
          <DropsGrid issueId={issueData.id} issueUrl={issueData.issueUrl} />
        )}
      </PageContainer>
    </>
  )
}

const UpgradePrompt = ({ onUpgrade }: { onUpgrade: () => void }) => {
  return (
    <UpgradeContainer>
      <UpgradeCard>
        <UpgradeIconWrapper>
          <img src="/images/beyond-icon.png" alt="Beyond" />
        </UpgradeIconWrapper>
        <UpgradeTitle>Unlock Beyond Content</UpgradeTitle>
        <UpgradeDescription>
          Upgrade to Beyond to access exclusive drops, behind-the-scenes content, and more.
        </UpgradeDescription>
        <UpgradeButton onClick={onUpgrade}>
          Upgrade to Beyond
        </UpgradeButton>
      </UpgradeCard>
    </UpgradeContainer>
  )
}

const DropsGrid = ({ issueId, issueUrl }: { issueId: string; issueUrl: string }) => {
  const { drops, isLoading, hasMore, fetchDrops, loadMore } = useGetDrops(issueId)
  const router = useRouter()

  useEffect(() => {
    if (issueId) {
      fetchDrops(0)
    }
  }, [issueId, fetchDrops])

  const isInitialLoading = isLoading && drops.length === 0

  if (isInitialLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>Loading drops...</LoadingText>
      </LoadingContainer>
    )
  }

  const navigateToDrop = (drop: any) => {
    // Use entry ID for unique identification
    const dropId = drop.sys.id
    router.push(`/issues/${issueUrl}/${dropId}`)
  }

  return (
    <InfiniteScroll
      dataLength={drops.length}
      next={loadMore}
      hasMore={hasMore}
      scrollThreshold="400px"
      loader={
        <LoadingMore>
          <LoadingSpinner small />
          <LoadingText>Loading more...</LoadingText>
        </LoadingMore>
      }
    >
      <GridContainer>
        {drops.map((drop: any, index: number) => (
          <DropCard
            key={drop.sys.id}
            onClick={() => navigateToDrop(drop)}
            style={{ animationDelay: `${(index % 40) * 0.02}s` }}
          >
            <DropImageWrapper>
              {drop.fields?.cover?.fields?.file?.url && (
                <DropImage
                  src={urlParser(drop.fields.cover.fields.file.url, {
                    width: 500,
                    format: 'webp',
                    quality: 85
                  })}
                  alt={drop.fields.title}
                />
              )}
              <DropOverlay>
                <ViewButton>View</ViewButton>
              </DropOverlay>
            </DropImageWrapper>
            <DropTitle>{drop.fields.title}</DropTitle>
          </DropCard>
        ))}
      </GridContainer>
    </InfiniteScroll>
  )
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`

const spin = keyframes`
  to { transform: rotate(360deg); }
`

const PageContainer = styled.div`
  min-height: 100vh;
  background: #0a0a0a;
  padding: 16px 24px 60px;

  @media (min-width: 800px) {
    padding: 24px 48px 80px;
  }
`

const Header = styled.div`
  text-align: center;
  margin-bottom: 16px;
  animation: ${fadeIn} 0.6s ease-out;
`

const TierToggle = styled.div`
  display: inline-flex;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 4px;
  margin-bottom: 24px;
  gap: 4px;
`

const ToggleOption = styled.button<{ active: boolean; locked?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.12)' : 'transparent'};
  color: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 13px;
  letter-spacing: 1px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s ease;
  height: auto;

  &:hover {
    background: ${props => props.active ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.05)'};
    color: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.7)'};
  }

  @media (max-width: 500px) {
    padding: 8px 14px;
    font-size: 11px;
    gap: 6px;
  }
`

const ToggleIcon = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;

  @media (max-width: 500px) {
    width: 20px;
    height: 20px;
  }
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  max-width: 1400px;
  margin: 0 auto;

  @media (min-width: 600px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }

  @media (min-width: 1000px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
  }
`

const DropCard = styled.div`
  cursor: pointer;
  animation: ${fadeIn} 0.3s ease-out both;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-4px);
  }
`

const DropImageWrapper = styled.div`
  position: relative;
  aspect-ratio: 3/4;
  border-radius: 8px;
  overflow: hidden;
  background: #151515;
`

const DropImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;

  ${DropCard}:hover & {
    transform: scale(1.05);
  }
`

const DropOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${DropCard}:hover & {
    opacity: 1;
  }
`

const ViewButton = styled.span`
  font-size: 12px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: white;
  font-family: 'Soehne-Kraftig', sans-serif;
  padding: 10px 20px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 4px;
  transition: all 0.2s ease;

  ${DropCard}:hover & {
    background: white;
    color: #0a0a0a;
  }
`

const DropTitle = styled.h3`
  font-size: 14px;
  color: white;
  font-family: 'Soehne-XLeicht', sans-serif;
  text-align: center;
  margin-top: 12px;
  padding: 0 4px;
  transition: color 0.2s ease;

  ${DropCard}:hover & {
    color: rgba(255, 255, 255, 0.8);
  }

  @media (min-width: 800px) {
    font-size: 16px;
  }
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  gap: 16px;
`

const LoadingMore = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  gap: 12px;
`

const LoadingSpinner = styled.div<{ small?: boolean }>`
  width: ${props => props.small ? '20px' : '32px'};
  height: ${props => props.small ? '20px' : '32px'};
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-top-color: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`

const LoadingText = styled.span`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Soehne-Leicht', sans-serif;
  letter-spacing: 1px;
`

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
`

const ErrorText = styled.p`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Soehne-Leicht', sans-serif;
`

const LockIcon = styled.span`
  font-size: 12px;
  margin-left: 4px;
`

const UpgradeContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  padding: 20px;
`

const UpgradeCard = styled.div`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  padding: 48px 40px;
  max-width: 420px;
  text-align: center;
  animation: ${fadeIn} 0.5s ease-out;
  backdrop-filter: blur(10px);
`

const UpgradeIconWrapper = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 50px;
    height: 50px;
    object-fit: contain;
  }
`

const UpgradeTitle = styled.h2`
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 24px;
  color: white;
  margin: 0 0 16px;
  letter-spacing: 0.5px;
`

const UpgradeDescription = styled.p`
  font-family: 'Soehne-Leicht', sans-serif;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin: 0 0 32px;
`

const UpgradeButton = styled.button`
  background: linear-gradient(135deg, #556B2F 0%, #6B8E23 100%);
  border: none;
  border-radius: 8px;
  color: white;
  font-family: 'Soehne-Kraftig', sans-serif;
  font-size: 14px;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 14px 32px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(85, 107, 47, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`
